import { CONSTANTS } from "@/constants";
import { PaginationResponse } from "@/interfaces/api.interface";
import {
  FetchPropertiesRequest,
  Location,
  Property,
  PropertyGeoJSON,
  PropertyMetadata,
  YallaValuePropertiesRequest,
} from "@/interfaces/property.interface";
import { handleApiError } from "@/lib/api/error";
import { logger } from "@/lib/logger";
import { useAuth } from "@clerk/nextjs";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fromPairs, toPairs } from "lodash";
import { useLocale } from "next-intl";
import { cache } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildQueryParams = (fetchProperties: Record<string, any>) => {
  return Object.entries(fetchProperties)
    .filter(([, value]) => value !== undefined && value !== null)
    .reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value.join(",");
      } else {
        acc[key] = value.toString();
      }
      return acc;
    }, {} as Record<string, string>);
};

const fetchProperty = cache(
  async (id: string, locale: string, token?: string): Promise<Property> => {
    const response = await fetch(
      `${CONSTANTS.API_URL}/properties/property/${id}`,
      {
        cache: "force-cache",
        headers: {
          "Accept-Language": locale,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    if (!response.ok) {
      const error = await handleApiError(response);

      logger.error(
        {
          error,
          id,
          locale,
        },
        "Unable to fetch property. Please try again later."
      );
      throw new Error("Unable to fetch property. Please try again later.");
    }

    return response.json();
  }
);

const fetchSimilarProperties = cache(
  async (id: string, locale: string, token?: string): Promise<Property[]> => {
    const response = await fetch(
      `${CONSTANTS.API_URL}/properties/property/${id}/similar`,
      {
        cache: "force-cache",
        headers: {
          "Accept-Language": locale,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    if (!response.ok) {
      const error = await handleApiError(response);
      logger.error(
        {
          error,
          id,
          locale,
        },
        "Failed to fetch similar properties"
      );
      throw new Error("Unable to fetch similar properties.");
    }

    return response.json();
  }
);

const fetchLocationAncestor = async (
  slug: string,
  locale: string
): Promise<
  Pick<Location, "level" | "name" | "path" | "pathName" | "fullSlug">[]
> => {
  const response = await fetch(
    `${CONSTANTS.API_URL}/properties/ancestors/${encodeURIComponent(slug)}`,
    {
      cache: "force-cache",
      headers: { "Accept-Language": locale },
    }
  );

  if (!response.ok) {
    const error = await handleApiError(response);
    logger.error(error, "Failed to fetch location ancestor");
    throw new Error("Unable to fetch location ancestor.");
  }

  return response.json();
};

const fetchProperties = cache(
  async (
    fetchPropertiesRequest: FetchPropertiesRequest,
    locale: string,
    token?: string
  ): Promise<PaginationResponse<Property>> => {
    const queryString = new URLSearchParams(
      buildQueryParams(fetchPropertiesRequest)
    ).toString();

    const response = await fetch(
      `${CONSTANTS.API_URL}/properties/?${queryString}`,
      {
        cache: "force-cache",
        headers: {
          "Accept-Language": locale,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    if (!response.ok) {
      const error = await handleApiError(response);
      logger.error(
        { error, fetchPropertiesRequest, locale },
        "Error fetching properties"
      );
      throw new Error("Unable to fetch properties.");
    }

    const properties = (await response.json()) as PaginationResponse<Property>;

    return properties;
  }
);

const fetchShowCaseProperty = cache(
  async (locale: string, token?: string): Promise<Property> => {
    const response = await fetch(`${CONSTANTS.API_URL}/properties/showcase`, {
      cache: "force-cache",
      headers: {
        "Accept-Language": locale,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const error = await handleApiError(response);
      logger.error(
        {
          error,
          locale,
        },
        "Failed to fetch showcase property"
      );
      throw new Error("Unable to fetch showcase property.");
    }

    return response.json();
  }
);

const fetchYallaValueProperties = async (
  data: YallaValuePropertiesRequest,
  locale: string,
  token?: string
): Promise<{ listings: { items: Property[] } }> => {
  const queryParams = new URLSearchParams(
    Object.entries(data) as [string, string][]
  ).toString();

  const response = await fetch(
    `${CONSTANTS.API_URL}/yalla-value/properties?${queryParams}`,
    {
      cache: "force-cache",
      headers: {
        "Accept-Language": locale,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    const error = await handleApiError(response);
    logger.error(error, "Error fetching Yalla Value properties");
    throw new Error("Unable to fetch Yalla Value properties.");
  }

  return await response.json();
};

const searchProperties = async (
  name: string,
  locale: string
): Promise<Location[]> => {
  const response = await fetch(
    `${CONSTANTS.API_URL}/properties/autocomplete?search=${encodeURIComponent(
      name
    )}`,
    {
      cache: "force-cache",
      headers: { "Accept-Language": locale },
    }
  );

  if (!response.ok) {
    const error = await handleApiError(response);
    logger.error(
      {
        name,
        locale,
        error,
      },
      "Failed to search properties"
    );
    throw new Error("Unable to search properties.");
  }

  return response.json();
};

const fetchPropertiesMetadata = async (
  filter: Omit<FetchPropertiesRequest, "page" | "limit">,
  locale: string
): Promise<PropertyMetadata> => {
  const queryString = new URLSearchParams(buildQueryParams(filter)).toString();

  const response = await fetch(
    `${CONSTANTS.API_URL}/properties/metadata/?${queryString}`,
    {
      headers: { "Accept-Language": locale, cache: "force-cache" },
    }
  );

  if (!response.ok) {
    const error = await handleApiError(response);
    logger.error(error, "Failed to fetch properties metadata");
    throw new Error("Unable to fetch properties metadata.");
  }

  return response.json();
};

const getLocationsBySlugs = async (
  slugs: string[],
  locale: string
): Promise<Location[]> => {
  const response = await fetch(
    `${CONSTANTS.API_URL}/locations/slugs?paths=${slugs.join(",")}`,
    {
      cache: "force-cache",
      headers: { "Accept-Language": locale },
    }
  );

  if (!response.ok) {
    const error = await handleApiError(response);
    logger.error(error, "Failed to fetch locations by slugs");
    throw new Error("Unable to fetch locations by slugs.");
  }

  return response.json();
};

const fetchPropertyGeoJson = cache(
  async (
    filters: Omit<FetchPropertiesRequest, "page" | "list">,
    locale: string
  ): Promise<PropertyGeoJSON[]> => {
    const queryString = new URLSearchParams(
      buildQueryParams(filters)
    ).toString();

    const response = await fetch(
      `${CONSTANTS.API_URL}/properties/maps-geojson?${queryString}`,
      {
        cache: "force-cache",
        headers: { "Accept-Language": locale },
      }
    );

    if (!response.ok) {
      const error = await handleApiError(response);
      logger.error(error, "Failed to fetch property GeoJSON");
      throw new Error("Unable to fetch property GeoJSON.");
    }

    return response.json();
  }
);

const useGetPropertyShowcase = (locale: string) => {
  return useQuery({
    queryKey: ["PROPERTIES", "SHOWCASE", locale],
    queryFn: () => fetchShowCaseProperty(locale),
  });
};

const useGetPropertyGeoJson = (filters: Partial<FetchPropertiesRequest>) => {
  const locale = useLocale();
  delete filters["locations"];
  delete filters["limit"];
  delete filters["page"];
  delete filters["propertySearch"];
  delete filters["maxLatitude"];
  delete filters["minLatitude"];
  delete filters["maxLongitude"];
  delete filters["minLongitude"];

  return useQuery({
    queryKey: ["PROPERTIES", "GEO JSON", { ...filters }, locale],
    queryFn: () => fetchPropertyGeoJson(filters, locale),
  });
};

const useProperties = (fetchPropertiesRequest: FetchPropertiesRequest) => {
  const { getToken } = useAuth();
  const locale = useLocale();
  const orderedFilters = fromPairs(
    toPairs(fetchPropertiesRequest).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB)
    )
  );

  return useQuery({
    queryKey: ["PROPERTIES", { ...orderedFilters }, locale],
    queryFn: async () => {
      const token = await getToken();
      return await fetchProperties(fetchPropertiesRequest, locale, token!);
    },
  });
};

const useYallaValueProperties = (
  yallaValuePropertiesRequest: YallaValuePropertiesRequest
) => {
  const locale = useLocale();
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [
      "Yalla value",
      "PROPERTIES",
      { ...yallaValuePropertiesRequest },
      locale,
    ],
    queryFn: async () => {
      const token = await getToken();
      return fetchYallaValueProperties(
        yallaValuePropertiesRequest,
        locale,
        token!
      );
    },
  });
};

const usePropertiesMetadata = (
  fetchPropertiesRequest: Omit<FetchPropertiesRequest, "page" | "limit">
) => {
  const locale = useLocale();
  const orderedFilters = fromPairs(
    toPairs(fetchPropertiesRequest).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB)
    )
  );

  return useQuery({
    queryKey: ["PROPERTIES", "METADATA", { ...orderedFilters }, locale],
    queryFn: () => fetchPropertiesMetadata(fetchPropertiesRequest, locale),
  });
};

const useSearchProperties = (name: string) => {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: ["PROPERTIES", "SEARCH", name, locale],
    queryFn: ({}) => searchProperties(name, locale),
    enabled() {
      return false;
    },
    getNextPageParam: () => {
      return null;
    },
    initialPageParam: name,
  });
};

const useGetLocationBySlugs = (slugs: Array<string>) => {
  const locale = useLocale();

  return useQuery({
    queryKey: ["PROPERTIES", "LOCATION", "SLUGS", { slugs }, locale],
    queryFn: () => getLocationsBySlugs(slugs, locale),
    enabled: false,
  });
};

const useProperty = (id: string) => {
  const locale = useLocale();
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["PROPERTY", { id }, locale],
    queryFn: async () => {
      const token = await getToken();
      return await fetchProperty(id, locale, token!);
    },
    enabled: true,
  });
};

const useSimilarProperties = (id: string) => {
  const locale = useLocale();
  return useQuery({
    queryKey: ["PROPERTIES", "SIMILAR", { id }, locale],
    queryFn: () => fetchSimilarProperties(id, locale),
    enabled: true,
  });
};

const useGetLocationAncestor = (fullSlug: string) => {
  const locale = useLocale();
  return useQuery({
    queryKey: ["PROPERTIES", "LOCATION", "ANCESTOR", fullSlug, locale],
    queryFn: () => fetchLocationAncestor(fullSlug, locale),
  });
};

export {
  fetchProperties,
  fetchPropertiesMetadata,
  fetchProperty,
  fetchShowCaseProperty,
  fetchSimilarProperties,
  getLocationsBySlugs,
  searchProperties,
  useGetLocationAncestor,
  useGetLocationBySlugs,
  useGetPropertyGeoJson,
  useGetPropertyShowcase,
  useProperties,
  usePropertiesMetadata,
  useProperty,
  useSearchProperties,
  useSimilarProperties,
  useYallaValueProperties,
};
